import React, { useMemo, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import type { DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import './SubscriptionPeriodPicker.css';
import { formatYMD, parseYMD, stringsToDateRange } from '../utils/dateUtils';

const fmtDisplay = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

/** Inclusive [start, end] for gray display */
export type SubscriptionBusyPeriod = { start: string; end: string };

type Props = {
  startDate: string;
  endDate: string;
  onChange: (next: { startDate: string; endDate: string }) => void;
  /** Other subscriptions in the same group (inclusive calendar range per row) */
  busyPeriods: SubscriptionBusyPeriod[];
  disabled?: boolean;
  errors?: { startDate?: string; endDate?: string };
};

/** Gray background: inclusive occupied range */
function isBusyGray(date: Date, periods: SubscriptionBusyPeriod[]): boolean {
  const y = formatYMD(date);
  return periods.some((p) => y >= p.start && y <= p.end);
}

/**
 * Disable days strictly inside [start, end) — same rule as API start overlap:
 * cannot pick d where existing.start <= d < existing.end (touching existingEnd allowed).
 */
function isOverlapDisabled(date: Date, periods: SubscriptionBusyPeriod[]): boolean {
  const y = formatYMD(date);
  return periods.some((p) => y >= p.start && y < p.end);
}

const SubscriptionPeriodPicker: React.FC<Props> = ({
  startDate,
  endDate,
  onChange,
  busyPeriods,
  disabled,
  errors,
}) => {
  const selected = useMemo(() => stringsToDateRange(startDate, endDate), [startDate, endDate]);

  const [month, setMonth] = useState<Date>(() => {
    if (startDate) return parseYMD(startDate);
    if (busyPeriods[0]?.start) return parseYMD(busyPeriods[0].start);
    return new Date();
  });

  // When startDate changes externally, jump the calendar to that month while
  // still letting the user navigate freely via onMonthChange. Adjusting state
  // during render (the documented pattern) avoids a cascading-render lint error.
  const [prevStartDate, setPrevStartDate] = useState(startDate);
  if (prevStartDate !== startDate) {
    setPrevStartDate(startDate);
    if (startDate) {
      setMonth(parseYMD(startDate));
    }
  }

  const busyModifier = useMemo(
    () => ({
      busy: (date: Date) => isBusyGray(date, busyPeriods),
    }),
    [busyPeriods]
  );

  const busyClassNames = useMemo(
    () => ({
      busy: 'subscription-period-picker__day_busy',
    }),
    []
  );

  const handleSelect = (range: DateRange | undefined) => {
    if (!range?.from) {
      onChange({ startDate: '', endDate: '' });
      return;
    }
    if (range.from && range.to) {
      onChange({ startDate: formatYMD(range.from), endDate: formatYMD(range.to) });
      return;
    }
    onChange({ startDate: formatYMD(range.from), endDate: '' });
  };

  const summary =
    startDate && endDate
      ? `${fmtDisplay.format(parseYMD(startDate))} → ${fmtDisplay.format(parseYMD(endDate))}`
      : startDate && !endDate
        ? `${fmtDisplay.format(parseYMD(startDate))} — select end date`
        : null;

  return (
    <div className="subscription-period-picker">
      <div className="subscription-period-picker__header">
        <label className="subscription-period-picker__title">Subscription period *</label>
      </div>

      <p className="subscription-period-picker__hint">
        First click: start date. Second click: end date. Shaded dates are the student's other subscriptions.
        You may start a new subscription on the day the previous one ends.
      </p>

      {summary && <p className="subscription-period-picker__summary">{summary}</p>}

      <div className="subscription-period-picker__calendar-wrap">
        <DayPicker
          mode="range"
          selected={selected}
          onSelect={handleSelect}
          month={month}
          onMonthChange={setMonth}
          disabled={(date) => Boolean(disabled) || isOverlapDisabled(date, busyPeriods)}
          modifiers={busyModifier}
          modifiersClassNames={busyClassNames}
          className="subscription-period-picker__rdp"
        />
      </div>

      {(errors?.startDate || errors?.endDate) && (
        <div className="subscription-period-picker__errors">
          {errors.startDate && <span className="error-text">{errors.startDate}</span>}
          {errors.endDate && <span className="error-text">{errors.endDate}</span>}
        </div>
      )}
    </div>
  );
};

export default SubscriptionPeriodPicker;
